<template>
  <li class="cart-item">
    <div
      v-if="loading"
      class="cart-item__loading">
      <v-progress-circular
        v-if="loading"
        indeterminate
        color="primary" />
    </div>

    <div
      v-if="!loading"
      class="cart-item__image-wrapper">
      <img
        :src="imagePath"
        class="cart-item__image">
    </div>


    <div
      v-if="!loading"
      class="cart-item__details">
      <h4
        v-if="!favorites"
        class="cart-item__name">
        {{ itemData.name }}
      </h4>
      <router-link
        v-if="favorites && itemSet === 'hats'"
        v-ripple
        :to="{
          name: 'hat-product',
          params: {slug: itemData.slug}
        }"
        class="cart-item__name">
        {{ itemData.name }}
      </router-link>
      <router-link
        v-if="favorites && itemSet === 'patches'"
        v-ripple
        :to="{
          name: 'patch-product',
          params: {
            patchGroup: 'catalog',
            id: itemData.imageId
          }
        }"
        class="cart-item__name">
        {{ itemData.name }}
      </router-link>
      <div
        v-if="!favorites"
        class="cart-item__quantity">
        <v-btn
          v-if="!checkout"
          icon
          flat
          small
          class="cart-item__quantity-button"
          :disabled="cartItem.quantity <= 1"
          @click="decreaseQuantity">
          <v-icon>chevron_left</v-icon>
        </v-btn>
        <span
          v-if="checkout"
          class="cart-item__quantity-display cart-item__quantity-display--checkout">
          Quantity:
        </span>
        <span class="cart-item__quantity-display">
          {{ cartItem.quantity }}
        </span>
        <v-btn
          v-if="!checkout"
          icon
          flat
          small
          class="cart-item__quantity-button"
          @click="increaseQuantity">
          <v-icon>chevron_right</v-icon>
        </v-btn>
      </div>
      <div
        class="cart-item__price"
        :class="{
          'cart-item__price--checkout': checkout,
          'cart-item__price--favorites': favorites
        }">
        <span v-if="!favorites">
          {{ cartPrice | price }}
        </span>
        <span
          v-if="favorites && itemData.retailPrice"
          class="cart-item__retail-price"
          :class="{
            'cart-item__retail-price--sale': itemData.salePrice
          }">
          {{ itemData.retailPrice | price }}
        </span>
        <span
          v-if="favorites && itemData.retailPrice && itemData.salePrice"
          class="cart-item__sale-price">
          {{ itemData.salePrice | price }}
        </span>
        <span
          v-if="favorites && itemSet==='patches'"
          class="cart-item__retail-price">
          {{ patchPrice | price }}
        </span>
      </div>
    </div>

    <div
      v-if="!loading && !checkout"
      class="cart-item__controls">
      <v-icon
        v-if="!favorites"
        class="remove-button"
        @click="removeFromCart">
        close
      </v-icon>

      <v-icon
        v-if="showAddToFavorites"
        :disabled="itemInFavorites || !isUser"
        class="favorite-button"
        @click="setInFavorites">
        {{ itemInFavorites ? 'star' : 'star_border' }}
      </v-icon>

      <v-tooltip
        v-if="!showAddToFavorites && !favorites"
        bottom>
        <span v-if="isUser">Only patches in the permanent catalog<br>
          can be saved as favorites</span>
        <span v-else>Must be logged in to save items to favorites</span>
        <v-icon
          slot="activator"
          color="accent"
          disabled
          small>
          block
        </v-icon>
      </v-tooltip>

      <v-icon
        v-if="favorites"
        class="remove-from-favorites-button"
        @click="setInFavorites">
        close
      </v-icon>

      <v-btn
        v-if="favorites"
        icon
        outline
        color="primary"
        :disabled="!inStock({itemSet, itemData})"
        class="add-to-cart-button"
        @click="moveToCart">
        <v-icon>shopping_cart</v-icon>
      </v-btn>
    </div>
  </li>
</template>

<script>
import api from '../../utils/api';

export default {
  name: 'CartItem',
  props: {
    checkout: Boolean,
    favorites: Boolean,
    item: [Object, String],
    itemSet: String,
  },
  data() {
    return {
      cartItem: this.item,
      cartItemSet: this.itemSet,
      loading: false,
      errorMessage: '',
      itemData: {},
      imagePath: '',
    };
  },
  computed: {
    isUser() {
      return this.$store.getters['auth/isAuthenticated'];
    },
    hatsConfig() {
      return this.$store.state.hats.hatsConfig;
    },
    hatImageDir() {
      return this.$store.state.hats.imageDir;
    },
    patchImageDir() {
      return this.$store.state.patches.imageDir;
    },
    customsImageDir() {
      return this.$store.state.patches.customsDir;
    },
    patchPrice() {
      return this.$store.state.cart.catalogPatchPrice;
    },
    cartPrice() {
      return this.cartItem.unitPrice * this.cartItem.quantity;
    },
    itemInFavorites() {
      if (this.isUser && this.$store.state.user.currentUser !== null) {
        const favoritesStore = this.$store.state.user.currentUser.favorites;
        const itemIndex = favoritesStore[this.itemSet].indexOf(this.item.slug || this.item.imageId);
        if (itemIndex >= 0) {
          return true;
        }
        return false;
      }
      return false;
    },
    showAddToFavorites() {
      if (!this.isUser) return false;
      if (this.favorites) return false;
      if (this.itemSet === 'customPatches') return false;
      if (this.item.patchGroup === 'submissions') return false;
      if (this.item.patchGroup === 'savedCustoms') return false;
      return true;
    },
  },
  created() {
    this.loading = true;
    this.getItemData();
    if (this.hatsConfig === {}) {
      this.$store.dispatch('hats/getHatsConfig');
    }
  },
  methods: {
    getItemData() {
      // BEGIN COMPLEX CASE-HANDLING
      let itemCase;
      const set = this.itemSet;
      const group = this.item.patchGroup;

      if (set === 'hats') itemCase = 'hat';
      if (set === 'patches' && this.favorites) itemCase = 'catalog';
      if (set === 'patches' && group === 'catalog') itemCase = 'catalog';
      if (set === 'patches' && group === 'submissions') itemCase = 'submission';
      if (set === 'patches' && group === 'savedCustoms') itemCase = 'savedCustom';
      if (set === 'customPatches') itemCase = 'custom';

      switch (itemCase) {
        case 'hat':
          api
            .getData(`/hats/${this.favorites ? this.item : this.item.slug}`)
            .then((res) => {
              this.itemData = res.data;
              this.setImagePath(itemCase);
              this.loading = false;
            })
            .catch((error) => {
              this.errorMessage = error;
              this.loading = false;
            });
          break;
        case 'catalog':
          api
            .getData(`/catalog/${this.favorites ? this.item : this.item.imageId}`)
            .then((res) => {
              this.itemData = res.data;
              this.itemData.patchGroup = group;
              this.setImagePath(itemCase);
              this.loading = false;
            })
            .catch((error) => {
              this.errorMessage = error;
              this.loading = false;
            });
          break;
        case 'submission':
          api
            .getData(`/submissions/${this.item.imageId}`)
            .then((res) => {
              this.itemData = res.data;
              this.itemData.patchGroup = group;
              this.setImagePath(itemCase);
              this.loading = false;
            })
            .catch((error) => {
              this.errorMessage = error;
              this.loading = false;
            });
          break;
        case 'savedCustom':
          api
            .getData(`/customs/${this.item.imageId}`)
            .then((res) => {
              this.itemData = res.data;
              this.itemData.name = 'custom patch';
              this.itemData.patchGroup = group;
              this.setImagePath(itemCase);
              this.loading = false;
            })
            .catch((error) => {
              this.errorMessage = error;
              this.loading = false;
            });
          break;
        case 'custom':
          this.itemData = this.item;
          this.itemData.name = 'custom patch';
          this.setImagePath(itemCase);
          this.loading = false;
          break;
        default: // if no itemCase, do nothing
          break;
      }
    },
    setImagePath(itemCase) {
      switch (itemCase) {
        case 'hat':
          this.imagePath = this.hatImageDir + this.itemData.images[0];
          break;
        case 'catalog':
          this.imagePath = `${this.patchImageDir + this.itemData.imageId}.png`;
          break;
        case 'submission':
          this.imagePath = `${this.customsImageDir + this.itemData.imageId}.png`;
          break;
        case 'savedCustom':
          this.imagePath = `${this.customsImageDir + this.itemData.imageId}.png`;
          break;
        case 'custom':
          this.imagePath = this.itemData.dataURL;
          break;
        default: // if no itemCase, do nothing
          break;
      }
    },
    // add to cart from favorites
    moveToCart() {
      const item = this.itemData;
      const { itemSet } = this;
      switch (itemSet) {
        case 'hats':
          this.$store.dispatch('cart/addHatToCart', item);
          break;
        case 'patches':
          this.$store.dispatch('cart/addPatchToCart', { patch: item, patchGroup: 'catalog' });
          break;
        default: // if no itemSet, do nothing
          break;
      }
      const itemId = this.item;
      this.$store.dispatch('user/toggleItemInFavorites', { itemSet, itemId });
    },
    setInFavorites() {
      let itemId;
      if (this.favorites) { itemId = this.item; }
      if (this.item.slug) { itemId = this.item.slug; }
      if (this.item.imageId) { itemId = this.item.imageId; }
      const { itemSet } = this;
      this.$store.dispatch('user/toggleItemInFavorites', { itemSet, itemId });
    },
    removeFromCart() {
      const { item } = this;
      const { itemSet } = this;
      this.$store.dispatch('cart/removeFromCart', { itemSet, item });
    },
    increaseQuantity() {
      const { item } = this;
      const { itemSet } = this;
      this.$store.dispatch('cart/increaseQuantity', { itemSet, item });
    },
    decreaseQuantity() {
      const { item } = this;
      const { itemSet } = this;
      this.$store.dispatch('cart/decreaseQuantity', { itemSet, item });
    },
    inStock({ itemSet, itemData }) {
      if (itemSet === 'hats') {
        return itemData.quantityInStock >= this.hatsConfig.inventoryBuffer;
      } return true;
    },
  },
};
</script>

<style lang="stylus" scoped>
@require('../../stylus/base')

.cart-item
  display: flex
  flex-wrap: nowrap
  align-items: flex-start
  justify-content: space-between
  &+.cart-item
    padding-top: $base-padding

  &__loading
    display: flex
    flex: 1 1 0
    align-items: center
    justify-content: center
    padding: $base-padding*2

  &__image-wrapper
    display: flex
    flex: 1 0 6rem
    flex-flow: row wrap
    align-items: flex-start
    justify-content: center
    padding-top:$base-padding*.5

  &__image
    width: 100%

  &__details
    display: flex
    flex: 1 1 100%
    flex-flow: row wrap
    align-items: stretch
    align-content: flex-start
    justify-content: space-between

  &__name
    flex: 1 1 100%
    padding-left: $base-padding
    font-size: .9rem
    font-weight: $base-font-weight
    color: inherit
    text-decoration: none
    border-radius: $base-border-radius

  &__quantity
    display: flex
    flex-wrap: nowrap
    align-items: center
    justify-content: space-between
    &-button
      margin: 0
    &-display
      font-weight: $base-header-font-weight + 100
      &--checkout
        padding-right: $base-padding*.5
        padding-left: $base-padding

  &__price
    align-self: flex-end
    padding-bottom: $base-padding*.25
    font-weight: $base-header-font-weight + 100
    &--checkout,
    &--favorites
      font-weight: $base-font-weight
    &--favorites
      padding-left: $base-padding
      color: $accent--dark
  &__retail-price
    &--sale
      opacity: .5
      text-decoration: line-through
  &__sale-price
    color: $primary
    font-style: italic
    font-weight: $base-header-font-weight

  &__controls
    display: flex
    flex-flow: column nowrap
    align-items: center
    justify-content: space-between
    padding-left: $base-padding*.5

.remove-button
  &:hover
    color: $error
.favorite-button
  &:hover
    color: $warning
.remove-from-favorites-button
  &:hover
    color: $error
.add-to-cart-button
  margin: 0
  margin-top: $base-padding

</style>
